import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Loader2, RefreshCw, Database } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface CleaningStats {
  total: number;
  incomplete: number;
  needsCleaning: number;
}

interface CleaningResult {
  processed: number;
  updated: number;
  skipped: number;
  errors: number;
  results: Array<{
    id: string;
    status: string;
    reason: string;
  }>;
}

export function ToolsCleaner() {
  const { toast } = useToast();
  const [stats, setStats] = useState<CleaningStats | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleaningProgress, setCleaningProgress] = useState(0);
  const [results, setResults] = useState<CleaningResult | null>(null);

  const analyzeData = async () => {
    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('clean-tools', {
        body: { action: 'analyze' }
      });

      if (error) throw error;

      setStats(data.stats);
      toast({
        title: "Analysis Complete",
        description: `Found ${data.stats.needsCleaning} tools that need cleaning`,
      });
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const cleanTools = async (batchSize = 50) => {
    if (!stats) return;

    setIsCleaning(true);
    setCleaningProgress(0);
    setResults(null);

    try {
      const totalBatches = Math.ceil(stats.needsCleaning / batchSize);
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let totalErrors = 0;

      for (let batch = 0; batch < totalBatches; batch++) {
        const offset = batch * batchSize;
        
        const { data, error } = await supabase.functions.invoke('clean-tools', {
          body: { 
            action: 'clean',
            limit: batchSize,
            offset: offset
          }
        });

        if (error) throw error;

        totalProcessed += data.processed;
        totalUpdated += data.updated;
        totalSkipped += data.skipped;
        totalErrors += data.errors;

        setCleaningProgress(((batch + 1) / totalBatches) * 100);

        toast({
          title: `Batch ${batch + 1}/${totalBatches} Complete`,
          description: `Processed ${data.processed}, Updated ${data.updated}`,
        });

        // Small delay between batches
        if (batch < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      const finalResults = {
        processed: totalProcessed,
        updated: totalUpdated,
        skipped: totalSkipped,
        errors: totalErrors,
        results: []
      };

      setResults(finalResults);
      
      toast({
        title: "Cleaning Complete!",
        description: `Successfully updated ${totalUpdated} tools`,
      });

      // Refresh stats
      await analyzeData();

    } catch (error) {
      console.error('Cleaning error:', error);
      toast({
        title: "Cleaning Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsCleaning(false);
      setCleaningProgress(0);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Tools Database Cleaner
          </CardTitle>
          <CardDescription>
            Analyze and clean your tools database to ensure all entries are complete and standardized
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Analysis Section */}
          <div className="space-y-4">
            <Button 
              onClick={analyzeData} 
              disabled={isAnalyzing || isCleaning}
              className="w-full"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing Database...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Analyze Database
                </>
              )}
            </Button>

            {stats && (
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{stats.total}</div>
                    <p className="text-xs text-muted-foreground">Total Tools</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">{stats.needsCleaning}</div>
                    <p className="text-xs text-muted-foreground">Need Cleaning</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{stats.total - stats.needsCleaning}</div>
                    <p className="text-xs text-muted-foreground">Complete</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Cleaning Section */}
          {stats && stats.needsCleaning > 0 && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-orange-900">Data Issues Found</h4>
                    <p className="text-sm text-orange-700">
                      {stats.needsCleaning} tools are missing critical information like categories, URLs, logos, or use cases.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => cleanTools()} 
                disabled={isCleaning || isAnalyzing}
                className="w-full"
                size="lg"
              >
                {isCleaning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cleaning Tools... {Math.round(cleaningProgress)}%
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Clean & Enrich All Tools
                  </>
                )}
              </Button>

              {isCleaning && (
                <div className="space-y-2">
                  <Progress value={cleaningProgress} className="w-full" />
                  <p className="text-sm text-muted-foreground text-center">
                    Processing tools with AI enrichment and web scraping...
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Results Section */}
          {results && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Cleaning Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{results.processed}</div>
                    <Badge variant="secondary">Processed</Badge>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{results.updated}</div>
                    <Badge variant="secondary">Updated</Badge>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{results.skipped}</div>
                    <Badge variant="secondary">Skipped</Badge>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{results.errors}</div>
                    <Badge variant="secondary">Errors</Badge>
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">
                    ✅ Successfully processed {results.updated} tools with AI-powered enrichment!
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* What This Does */}
          <Card>
            <CardHeader>
              <CardTitle>What This Cleaner Does</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span><strong>AI Enrichment:</strong> Uses OpenAI to generate proper categories, use cases, and descriptions</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span><strong>Web Scraping:</strong> Finds missing URLs and logos automatically</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span><strong>Standardization:</strong> Ensures consistent formatting for all fields</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span><strong>Safe Processing:</strong> Skips already complete tools and handles errors gracefully</span>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}